import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import truckDocumentService from '../services/truckDocumentService';

/**
 * Upload one document and attach it to every non-retired fleet truck.
 */
export default function FleetDocumentUploadDialog({ open, onClose, truckCount = 0, onComplete }) {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setDocumentType('');
    setDisplayName('');
    setIssueDate('');
    setExpiryDate('');
    setFile(null);
    setError(null);
    setResult(null);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    let mounted = true;
    (async () => {
      try {
        setLoadingTypes(true);
        const res = await truckDocumentService.getDocumentTypes();
        if (!mounted) return;
        const types = Array.isArray(res?.data) ? res.data : [];
        setDocumentTypes(types);
      } catch (err) {
        if (mounted) setError('Failed to load document types');
      } finally {
        if (mounted) setLoadingTypes(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, reset]);

  const handleTypeChange = (value) => {
    setDocumentType(value);
    const match = documentTypes.find((t) => t.value === value);
    if (match && !displayName) setDisplayName(match.name);
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    if (!documentType) {
      setError('Select a document type');
      return;
    }
    if (!file) {
      setError('Choose a file to upload');
      return;
    }
    try {
      setSubmitting(true);
      const data = await truckDocumentService.createFleetWideDocument({
        documentType,
        displayName: displayName || undefined,
        issueDate: issueDate || undefined,
        expiryDate: expiryDate || undefined,
      }, file);
      setResult(data?.data || data);
      if (typeof onComplete === 'function') onComplete(data?.data || data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Fleet upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload to all trucks</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            One file is copied onto every truck in the fleet
            {truckCount > 0 ? ` (${truckCount} units)` : ''}.
            If a truck already has this document type, it is replaced.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          )}
          {result && (
            <Alert severity="success">
              Applied to {(result.created || 0) + (result.updated || 0)} trucks
              {result.created ? ` · ${result.created} new` : ''}
              {result.updated ? ` · ${result.updated} updated` : ''}
              {result.failed ? ` · ${result.failed} failed` : ''}
            </Alert>
          )}

          <FormControl fullWidth size="small" disabled={loadingTypes || submitting || Boolean(result)}>
            <InputLabel id="fleet-doc-type-label">Document type</InputLabel>
            <Select
              labelId="fleet-doc-type-label"
              label="Document type"
              value={documentType}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {documentTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Display name (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={submitting || Boolean(result)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Issue date"
              InputLabelProps={{ shrink: true }}
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              disabled={submitting || Boolean(result)}
            />
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Expiry date"
              InputLabelProps={{ shrink: true }}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              disabled={submitting || Boolean(result)}
            />
          </Stack>

          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={submitting || Boolean(result)}
          >
            {file ? file.name : 'Choose file'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          {file && (
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          )}

          {submitting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Applying to fleet…</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || loadingTypes || !documentType || !file}
          >
            Apply to all trucks
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
